/**
 * shared/money — valor monetário em centavos inteiros (DD-008, RNF 11, ADR-0008).
 *
 * Regras de domínio:
 * - O valor canônico é SEMPRE `amountCents` (inteiro). Nunca usar float/double.
 * - `formatMoney` é o ÚNICO ponto de exibição monetária do portal.
 * - `aggregateByCurrency` agrega por moeda e NUNCA mistura moedas distintas (PBT-02).
 * - O cliente apenas exibe — nunca soma/converte moedas diferentes (RNF 11).
 *
 * Funções puras: este módulo não importa React nem qualquer dependência de UI.
 */

export type Currency = "BRL" | "USD" | "EUR";

/** Valor monetário imutável: centavos inteiros + moeda ISO-4217. */
export interface Money {
  readonly amountCents: number;
  readonly currency: Currency;
}

/** Resultado de agregação por moeda: `currency → totalCents` (sem mistura). */
export type MoneyByCurrency = Partial<Record<Currency, number>>;

// Cache de formatadores Intl por moeda (evita recriação a cada chamada).
const formatterCache = new Map<Currency, Intl.NumberFormat>();

function formatterFor(currency: Currency): Intl.NumberFormat {
  let fmt = formatterCache.get(currency);
  if (!fmt) {
    fmt = new Intl.NumberFormat("pt-BR", { style: "currency", currency });
    formatterCache.set(currency, fmt);
  }
  return fmt;
}

// O ICU insere NO-BREAK SPACE (U+00A0) e/ou NARROW NO-BREAK SPACE (U+202F)
// entre o símbolo da moeda e o número. Normalizamos para espaço comum a fim
// de manter a saída determinística e estável entre versões de ICU/Node.
const NON_BREAKING_SPACES = new RegExp("[\\u00A0\\u202F]", "g");

/**
 * Formata um `Money` em string pt-BR (ex.: `{ amountCents: 10000, currency: 'BRL' }`
 * → `"R$ 100,00"`). Ponto único de exibição monetária (RNF 11).
 *
 * A divisão por 100 ocorre apenas na borda de exibição; o valor canônico
 * permanece inteiro.
 */
export function formatMoney(money: Money): string {
  const value = money.amountCents / 100;
  return formatterFor(money.currency).format(value).replace(NON_BREAKING_SPACES, " ");
}

// ---------------------------------------------------------------------------
// Entrada monetária (máscara dígitos-como-centavos) — usado nos formulários de
// oportunidade. Regra: o usuário digita apenas dígitos, interpretados como
// centavos, da direita para a esquerda (padrão de input monetário). Ex.: a
// sequência "2000000" vira "20.000,00". O valor canônico continua em centavos
// inteiros (RNF 11); a formatação só ocorre na borda de exibição.
// ---------------------------------------------------------------------------

// Formatador de entrada (sem símbolo de moeda — os labels já indicam "(R$)").
const inputFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Converte uma string de input monetário (ex.: "20.000,00") para centavos
 * inteiros, considerando apenas os dígitos (dígitos-como-centavos).
 * Strings sem dígito retornam 0.
 */
export function parseBRLInputToCents(display: string): number {
  const digits = display.replace(/\D/g, "");
  if (digits === "") return 0;
  return parseInt(digits, 10);
}

/**
 * Formata centavos inteiros para a string de exibição do input (ex.: 2000000 →
 * "20.000,00"). Por convenção, `0` retorna string vazia para que o placeholder
 * "0,00" permaneça visível em campos não preenchidos (ex.: criação).
 */
export function formatCentsToInput(cents: number): string {
  if (cents === 0) return "";
  return inputFormatter.format(cents / 100).replace(NON_BREAKING_SPACES, " ");
}

/**
 * Máscara viva para o `onChange` dos inputs de valor: normaliza o texto cru
 * digitado para a forma "#.###,##" preservando os dígitos como centavos.
 */
export function maskCentsInput(raw: string): string {
  return formatCentsToInput(parseBRLInputToCents(raw));
}

/**
 * Agrega uma lista de `Money` por moeda, somando apenas centavos da MESMA
 * moeda. Garante a invariante PBT-02: nenhuma moeda distinta é somada junta.
 */
export function aggregateByCurrency(items: readonly Money[]): MoneyByCurrency {
  const result: MoneyByCurrency = {};
  for (const item of items) {
    result[item.currency] = (result[item.currency] ?? 0) + item.amountCents;
  }
  return result;
}
